import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthHeaders, feetToMeters, metersToFeet } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { CheckpointMap } from "../components/Map/CheckpointMap";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Plus, Pencil, Trash2, Crosshair, Locate, Maximize2, Minimize2, QrCode, RotateCcw, Printer, LayoutGrid, Table as TableIcon } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Component to handle map clicks in the form dialog
const FormMapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

// Component to center map on coordinates
const CenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && Array.isArray(center) && center.length >= 2 && 
        !isNaN(center[0]) && !isNaN(center[1]) &&
        center[0] !== 0 && center[1] !== 0) {
      map.setView(center, zoom || 18, { animate: true });
    }
  }, [center, zoom, map]);
  return null;
};

export const Checkpoints = () => {
  const { user, isAdmin, isRevendedor } = useAuth();
  const [checkpoints, setCheckpoints] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [viewMode, setViewMode] = useState("tiles"); // "tiles" or "grid"
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isMapMaximized, setIsMapMaximized] = useState(false);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [resettingQr, setResettingQr] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    latitude: "",
    longitude: "",
    radius_feet: 50,
    company_id: "",
    description: "",
  });

  const fetchData = async (companyId = null) => {
    try {
      const params = new URLSearchParams();
      if (companyId && companyId !== "all") {
        params.append("company_id", companyId);
      }

      const [checkpointsRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/checkpoints?${params.toString()}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
      ]);
      setCheckpoints(Array.isArray(checkpointsRes.data) ? checkpointsRes.data : []);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
      setCheckpoints([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedCompany);
  }, [selectedCompany]);

  const handleOpenForm = (checkpoint = null) => {
    if (checkpoint) {
      setSelectedCheckpoint(checkpoint);
      setFormData({
        name: checkpoint.name,
        latitude: checkpoint.latitude,
        longitude: checkpoint.longitude,
        radius_feet: checkpoint.radius_feet,
        company_id: checkpoint.company_id,
        description: checkpoint.description || "",
      });
    } else {
      setSelectedCheckpoint(null);
      setFormData({
        name: "",
        latitude: "",
        longitude: "",
        radius_feet: 50,
        company_id: isAdmin() ? "" : user.company_id,
        description: "",
      });
    }
    setIsFormOpen(true);
  };

  const handleMapClick = (latlng) => {
    if (isFormOpen) {
      setFormData({
        ...formData,
        latitude: latlng.lat.toFixed(6),
        longitude: latlng.lng.toFixed(6),
      });
      toast.info("Coordenadas actualizadas desde el mapa");
    }
  };

  const handleGetCurrentLocation = async () => {
    if (!navigator.geolocation) {
      // Fallback: intentar obtener ubicación basada en IP
      toast.info("Geolocalización no disponible. Intentando obtener ubicación aproximada por IP...");
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        if (data.latitude && data.longitude) {
          const lat = parseFloat(data.latitude).toFixed(6);
          const lng = parseFloat(data.longitude).toFixed(6);
          setFormData({
            ...formData,
            latitude: lat,
            longitude: lng,
          });
          toast.success(`Ubicación aproximada obtenida (basada en IP): ${lat}, ${lng}. Puedes ajustarla haciendo clic en el mapa.`);
          return;
        }
      } catch (ipError) {
        console.error("IP geolocation error:", ipError);
      }
      toast.error("No se pudo obtener la ubicación. Por favor, haz clic en el mapa para seleccionar las coordenadas.");
      return;
    }

    toast.info("Obteniendo tu ubicación... (en PC puede tardar más, usa el mapa como alternativa)");
    
    // Primero intentamos con alta precisión pero permitiendo caché
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);
        setFormData({
          ...formData,
          latitude: lat,
          longitude: lng,
        });
        toast.success(`Ubicación obtenida: ${lat}, ${lng}`);
      },
      async (error) => {
        // Si falla con alta precisión, intentamos con configuración menos estricta
        if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
          toast.info("Reintentando con configuración menos estricta...");
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude.toFixed(6);
              const lng = position.coords.longitude.toFixed(6);
              setFormData({
                ...formData,
                latitude: lat,
                longitude: lng,
              });
              toast.success(`Ubicación obtenida: ${lat}, ${lng}`);
            },
            async (retryError) => {
              // Si falla también, intentamos con geolocalización por IP como fallback
              if (retryError.code === retryError.TIMEOUT || retryError.code === retryError.POSITION_UNAVAILABLE) {
                toast.info("Intentando obtener ubicación aproximada por IP...");
                try {
                  const response = await fetch("https://ipapi.co/json/");
                  const data = await response.json();
                  if (data.latitude && data.longitude) {
                    const lat = parseFloat(data.latitude).toFixed(6);
                    const lng = parseFloat(data.longitude).toFixed(6);
                    setFormData({
                      ...formData,
                      latitude: lat,
                      longitude: lng,
                    });
                    toast.success(`Ubicación aproximada obtenida (basada en IP): ${lat}, ${lng}. Puedes ajustarla haciendo clic en el mapa.`);
                    return;
                  }
                } catch (ipError) {
                  console.error("IP geolocation error:", ipError);
                }
              }
              
              let errorMessage = "Error al obtener ubicación";
              if (retryError.code === retryError.PERMISSION_DENIED) {
                errorMessage = "Permiso de ubicación denegado. Por favor, permite el acceso a tu ubicación en la configuración del navegador o haz clic en el mapa.";
              } else if (retryError.code === retryError.POSITION_UNAVAILABLE) {
                errorMessage = "Ubicación no disponible en este dispositivo. Haz clic en el mapa para seleccionar las coordenadas manualmente.";
              } else if (retryError.code === retryError.TIMEOUT) {
                errorMessage = "Tiempo de espera agotado. En PC, la geolocalización puede no funcionar. Haz clic en el mapa para seleccionar las coordenadas.";
              }
              toast.error(errorMessage);
              console.error("Geolocation error:", retryError);
            },
            { 
              enableHighAccuracy: false, // Menos preciso pero más rápido
              timeout: 30000, // 30 segundos
              maximumAge: 60000 // Permite usar ubicación en caché de hasta 1 minuto
            }
          );
        } else {
          let errorMessage = "Error al obtener ubicación";
          if (error.code === error.PERMISSION_DENIED) {
            errorMessage = "Permiso de ubicación denegado. Por favor, permite el acceso a tu ubicación en la configuración del navegador o haz clic en el mapa.";
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errorMessage = "Ubicación no disponible en este dispositivo. Haz clic en el mapa para seleccionar las coordenadas manualmente.";
          }
          toast.error(errorMessage);
          console.error("Geolocation error:", error);
        }
      },
      { 
        enableHighAccuracy: true,
        timeout: 20000, // Aumentado a 20 segundos
        maximumAge: 60000 // Permite usar ubicación en caché de hasta 1 minuto
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius_feet: parseFloat(formData.radius_feet),
      };

      if (selectedCheckpoint) {
        await axios.put(`${API_URL}/checkpoints/${selectedCheckpoint.id}`, data, {
          headers: getAuthHeaders(),
        });
        toast.success("Punto de control actualizado");
      } else {
        const response = await axios.post(`${API_URL}/checkpoints`, data, {
          headers: getAuthHeaders(),
        });
        toast.success("Punto de control creado");
        // Resetear el formulario después de crear
        setFormData({
          name: "",
          latitude: "",
          longitude: "",
          radius_feet: 50,
          company_id: isAdmin() ? "" : user.company_id,
          description: "",
        });
      }
      setIsFormOpen(false);
      // Pequeño delay para asegurar que el backend haya procesado
      setTimeout(() => {
      fetchData(selectedCompany);
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/checkpoints/${selectedCheckpoint.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Punto de control eliminado");
      setIsDeleteOpen(false);
      setSelectedCheckpoint(null);
      fetchData(selectedCompany);
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleResetQr = async () => {
    if (!selectedCheckpoint) return;
    setResettingQr(true);
    try {
      const response = await axios.post(
        `${API_URL}/checkpoints/${selectedCheckpoint.id}/reset-qr`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Código QR reseteado");
      // Actualizar el checkpoint seleccionado con el nuevo QR
      setSelectedCheckpoint(response.data);
      fetchData(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al resetear QR");
    } finally {
      setResettingQr(false);
    }
  };

  const handlePrintQr = () => {
    if (!selectedCheckpoint) return;
    
    // Usar API de QR code para generar imagen
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(selectedCheckpoint.qr_code)}`;
    
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Code - ${selectedCheckpoint.name}</title>
            <style>
              @media print {
                @page {
                  margin: 20mm;
                }
              }
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
                padding: 20px;
              }
              h1 {
                margin-bottom: 20px;
                color: #333;
                font-size: 24px;
              }
              .qr-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 15px;
              }
              .qr-image {
                border: 2px solid #333;
                padding: 10px;
                background: white;
                width: 300px;
                height: 300px;
              }
              .checkpoint-name {
                font-size: 18px;
                font-weight: bold;
                margin-top: 10px;
                color: #333;
              }
            </style>
          </head>
          <body>
            <h1>Código QR - Punto de Control</h1>
            <div class="qr-container">
              <img src="${qrApiUrl}" alt="QR Code" class="qr-image" onload="setTimeout(() => window.print(), 250)" />
              <div class="checkpoint-name">${selectedCheckpoint.name}</div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <div className="space-y-6" data-testid="checkpoints-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Puntos de Control
          </h1>
          <p className="text-slate-600 mt-1">
            Gestiona los puntos de ronda para vigilantes
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filtro por compañía - Solo para admin y revendedor */}
          {(isAdmin() || isRevendedor()) && (
            <Select
              value={selectedCompany}
              onValueChange={(value) => {
                setSelectedCompany(value);
                setLoading(true);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrar por compañía" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las compañías</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Switch de vista */}
          <div className="flex items-center gap-2 px-3 py-2 border rounded-md">
            <LayoutGrid className={`h-4 w-4 ${viewMode === "tiles" ? "text-primary" : "text-slate-400"}`} />
            <Switch
              checked={viewMode === "grid"}
              onCheckedChange={(checked) => setViewMode(checked ? "grid" : "tiles")}
            />
            <TableIcon className={`h-4 w-4 ${viewMode === "grid" ? "text-primary" : "text-slate-400"}`} />
          </div>
          <Button onClick={() => handleOpenForm()} data-testid="add-checkpoint-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Punto
          </Button>
        </div>
      </div>

      {/* Map */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5 text-primary" />
              Mapa de Puntos de Control
              <span className="text-sm font-normal text-slate-500 ml-2">
                (Click en el mapa para obtener coordenadas)
              </span>
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMapMaximized(true)}
              title="Maximizar mapa"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <CheckpointMap
            checkpoints={checkpoints}
            selectedCheckpoint={selectedCheckpoint}
            onCheckpointClick={(cp) => {
              setSelectedCheckpoint(cp);
            }}
            onMapClick={handleMapClick}
            height="500px"
          />
        </CardContent>
      </Card>

      {/* Maximized Map Dialog */}
      <Dialog open={isMapMaximized} onOpenChange={setIsMapMaximized}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] p-0 translate-x-[-50%] translate-y-[-50%] flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5 text-primary" />
                Mapa de Puntos de Control
                <span className="text-sm font-normal text-slate-500 ml-2">
                  (Click en el mapa para obtener coordenadas)
                </span>
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMapMaximized(false)}
                title="Minimizar mapa"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="px-6 pb-6 flex-1 overflow-hidden min-h-0">
            <CheckpointMap
              checkpoints={checkpoints}
              selectedCheckpoint={selectedCheckpoint}
              onCheckpointClick={(cp) => {
                setSelectedCheckpoint(cp);
              }}
              onMapClick={handleMapClick}
              height="100%"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkpoint List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Puntos ({Array.isArray(checkpoints) ? checkpoints.length : 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !Array.isArray(checkpoints) || checkpoints.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              No hay puntos de control registrados
            </p>
          ) : viewMode === "tiles" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {checkpoints.map((checkpoint, index) => (
                <div
                  key={checkpoint.id}
                  className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                    selectedCheckpoint?.id === checkpoint.id
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setSelectedCheckpoint(checkpoint)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{checkpoint.name}</p>
                        <p className="text-sm text-slate-500">{checkpoint.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-mono text-slate-500">
                      {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Radio: {checkpoint.radius_feet} pies ({feetToMeters(checkpoint.radius_feet).toFixed(1)}m)
                    </p>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenForm(checkpoint);
                      }}
                      data-testid={`edit-checkpoint-${checkpoint.id}`}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCheckpoint(checkpoint);
                        setIsQrDialogOpen(true);
                      }}
                      data-testid={`qr-checkpoint-${checkpoint.id}`}
                    >
                      <QrCode className="h-3 w-3 mr-1" />
                      QR
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCheckpoint(checkpoint);
                        setIsDeleteOpen(true);
                      }}
                      data-testid={`delete-checkpoint-${checkpoint.id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Coordenadas</TableHead>
                    <TableHead>Radio</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkpoints.map((checkpoint, index) => (
                    <TableRow
                      key={checkpoint.id}
                      className={`cursor-pointer ${
                        selectedCheckpoint?.id === checkpoint.id
                          ? "bg-orange-50"
                          : ""
                      }`}
                      onClick={() => setSelectedCheckpoint(checkpoint)}
                    >
                      <TableCell>
                        <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center font-bold text-xs">
                          {index + 1}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{checkpoint.name}</TableCell>
                      <TableCell className="text-slate-500">{checkpoint.description || "-"}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {checkpoint.radius_feet} pies ({feetToMeters(checkpoint.radius_feet).toFixed(1)}m)
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenForm(checkpoint);
                            }}
                            data-testid={`edit-checkpoint-${checkpoint.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCheckpoint(checkpoint);
                              setIsQrDialogOpen(true);
                            }}
                            data-testid={`qr-checkpoint-${checkpoint.id}`}
                          >
                            <QrCode className="h-3 w-3 mr-1" />
                            QR
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCheckpoint(checkpoint);
                              setIsDeleteOpen(true);
                            }}
                            data-testid={`delete-checkpoint-${checkpoint.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedCheckpoint ? "Editar Punto de Control" : "Nuevo Punto de Control"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Entrada Principal"
                required
                data-testid="checkpoint-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción del punto..."
                rows={2}
                data-testid="checkpoint-description-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Coordenadas *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGetCurrentLocation}
                  data-testid="get-current-location-btn"
                >
                  <Locate className="h-3 w-3 mr-1" />
                  Usar mi ubicación
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="latitude" className="text-xs text-slate-500">
                    Latitud
                  </Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    placeholder="18.478973"
                    required
                    className="font-mono"
                    data-testid="checkpoint-latitude-input"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude" className="text-xs text-slate-500">
                    Longitud
                  </Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    placeholder="-69.967669"
                    required
                    className="font-mono"
                    data-testid="checkpoint-longitude-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Haz click en el mapa para seleccionar coordenadas
              </p>
              
              {/* Interactive Map */}
              <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height: "300px" }}>
                <MapContainer
                  center={
                    formData.latitude && formData.longitude && !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude))
                      ? [parseFloat(formData.latitude), parseFloat(formData.longitude)]
                      : [18.478973, -69.967669]
                  }
                  zoom={formData.latitude && formData.longitude && !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude)) ? 18 : 15}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />
                  
                  <FormMapClickHandler
                    onMapClick={(latlng) => {
                      setFormData({
                        ...formData,
                        latitude: latlng.lat.toFixed(6),
                        longitude: latlng.lng.toFixed(6),
                      });
                      toast.info("Coordenadas actualizadas");
                    }}
                  />
                  
                  {/* Always render CenterMap to update when coordinates change */}
                  {formData.latitude && formData.longitude && 
                   !isNaN(parseFloat(formData.latitude)) && 
                   !isNaN(parseFloat(formData.longitude)) && (
                    <CenterMap
                      key={`center-${formData.latitude}-${formData.longitude}`}
                      center={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                      zoom={18}
                    />
                  )}
                  
                  {/* Marker for selected location */}
                  {formData.latitude && formData.longitude && 
                   !isNaN(parseFloat(formData.latitude)) && 
                   !isNaN(parseFloat(formData.longitude)) && (
                    <Marker
                      key={`marker-${formData.latitude}-${formData.longitude}`}
                      position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                    >
                      <Popup>
                        <div className="p-2">
                          <p className="font-bold text-slate-900">Ubicación seleccionada</p>
                          <p className="text-xs font-mono text-slate-500">
                            {formData.latitude}, {formData.longitude}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="radius_feet">Radio de proximidad (pies) *</Label>
              <Input
                id="radius_feet"
                type="number"
                min="10"
                max="500"
                value={formData.radius_feet}
                onChange={(e) =>
                  setFormData({ ...formData, radius_feet: parseInt(e.target.value) || 50 })
                }
                required
                data-testid="checkpoint-radius-input"
              />
              <p className="text-xs text-slate-500">
                ≈ {feetToMeters(formData.radius_feet || 50).toFixed(1)} metros
              </p>
            </div>

            {/* Solo admin ve selector de compañía */}
            {isAdmin() ? (
              <div className="space-y-2">
                <Label>Compañía *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger data-testid="checkpoint-company-select">
                    <SelectValue placeholder="Seleccionar compañía" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-checkpoint-btn">
                {selectedCheckpoint ? "Guardar Cambios" : "Crear Punto"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={isQrDialogOpen} onOpenChange={setIsQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Código QR - {selectedCheckpoint?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedCheckpoint?.qr_code && (
              <div className="flex flex-col items-center gap-4 p-4">
                <div className="bg-white p-4 rounded-lg border-2 border-slate-200">
                  <QRCodeSVG
                    value={selectedCheckpoint.qr_code}
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <p className="text-sm text-slate-600 text-center">
                  Escanea este código QR desde la aplicación de patrullaje para registrar el punto de control
                </p>
                <div className="flex gap-2 w-full">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handlePrintQr}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Imprimir
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleResetQr}
                    disabled={resettingQr}
                  >
                    <RotateCcw className={`h-4 w-4 mr-2 ${resettingQr ? "animate-spin" : ""}`} />
                    Resetear QR
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar punto de control?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el
              punto "{selectedCheckpoint?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-checkpoint"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
